// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { multiLine, stacked100Area, type LineSeries } from '../components/charts';
import { FUEL_COLOUR, FUEL_LABEL, evShare, fuelShare, type Fuel, type Mix } from '../fuels';
import { stateColour, STATE_ORDER } from '../data';
import { glossaryTerm } from '../glossary';
import { formatPercent } from '../utils/format';

type FuelMetric = 'bev' | 'ev' | 'diesel';

export function renderTrends(root: HTMLElement, ctx: ViewContext): void {
  const { national } = ctx.data;
  const years = national.trend.years;
  let fuelMetric: FuelMetric = 'bev';

  const shareOf = (mix: Mix, m: FuelMetric): number | null =>
    m === 'ev' ? evShare(mix) : fuelShare(mix, m as Fuel);

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Five years of change on the road</h1>
      <p class="view-sub">
        The fleet each ${glossaryTerm('registered', 'January')} snapshot, ${years[0]}–${years[years.length - 1]}. Battery-electric
        has grown fast off a tiny base — but diesel has risen every single year too. The fleet electrifies and dieselises
        at the same time.
      </p>
    </div>

    <div class="grid-2 wide">
      <div class="panel">
        <div class="panel-title">National fuel mix, ${years[0]}–${years[years.length - 1]}</div>
        <div class="panel-sub">Each column is 100% of the passenger fleet that year. Hover for the exact split.</div>
        <div class="chart-scroll" data-role="area"></div>
      </div>
      <div class="panel">
        <div class="panel-title" data-role="line-title"></div>
        <div class="panel-sub">
          <div class="seg" role="group" data-role="fuel" style="margin-top:4px">
            <button class="seg-btn" data-fuel="bev" aria-pressed="true">Electric</button>
            <button class="seg-btn" data-fuel="ev" aria-pressed="false">Electrified</button>
            <button class="seg-btn" data-fuel="diesel" aria-pressed="false">Diesel</button>
          </div>
        </div>
        <div class="chart-scroll" data-role="line" style="margin-top:var(--space-sm)"></div>
        <div class="legend" data-role="line-legend"></div>
      </div>
    </div>
  `;

  // National 100% area over 5 years.
  const areaSlot = root.querySelector('[data-role="area"]') as HTMLElement;
  areaSlot.appendChild(
    stacked100Area(
      years.map((y, i) => ({ year: y, mix: national.trend.national[i] })),
      { width: 520, height: 320 },
    ),
  );

  // Per-state (or national) line chart of the chosen fuel share.
  const lineSlot = root.querySelector('[data-role="line"]') as HTMLElement;
  const legendSlot = root.querySelector('[data-role="line-legend"]') as HTMLElement;
  const titleEl = root.querySelector('[data-role="line-title"]') as HTMLElement;

  const paintLine = () => {
    const label = fuelMetric === 'bev' ? 'Electric' : fuelMetric === 'ev' ? 'Electrified' : 'Diesel';
    titleEl.textContent = `${label} share by state`;
    const series: LineSeries[] = STATE_ORDER.map((st) => ({
      id: st,
      label: st,
      colour: stateColour(st),
      values: national.trend.states[st].map((mix) => shareOf(mix, fuelMetric)),
    }));
    lineSlot.innerHTML = '';
    lineSlot.appendChild(multiLine(years, series, { width: 520, height: 320, yFmt: (v) => `${v.toFixed(0)}%` }));
    legendSlot.innerHTML = STATE_ORDER.map((st) => `<span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${stateColour(st)}"></span>${st}</span>`).join('');
    root.querySelectorAll('[data-fuel]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-fuel') === fuelMetric)));
  };

  root.querySelector('[data-role="fuel"]')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-fuel]');
    if (!btn) return;
    fuelMetric = btn.getAttribute('data-fuel') as FuelMetric;
    paintLine();
  });
  paintLine();

  // Change table below.
  const changePanel = document.createElement('div');
  changePanel.className = 'panel';
  changePanel.style.marginTop = 'var(--space-lg)';
  const first = national.trend.national[0];
  const last = national.trend.national[national.trend.national.length - 1];
  const rowFor = (f: Fuel) => {
    const a = fuelShare(first, f) ?? 0;
    const b = fuelShare(last, f) ?? 0;
    const dir = b - a >= 0 ? '+' : '';
    return `<tr style="cursor:default"><td><span class="legend-swatch" style="display:inline-block;background:${FUEL_COLOUR[f]};margin-right:6px;vertical-align:middle"></span>${FUEL_LABEL[f]}</td><td class="t-num">${formatPercent(a)}</td><td class="t-num">${formatPercent(b)}</td><td class="t-num" style="color:${b - a >= 0 ? 'var(--status-good)' : 'var(--status-bad)'}">${dir}${(b - a).toFixed(2)} pts</td></tr>`;
  };
  changePanel.innerHTML = `
    <div class="panel-title">What moved, ${years[0]} → ${years[years.length - 1]}</div>
    <div class="panel-sub">National passenger fuel share, then and now.</div>
    <div class="table-scroll">
      <table class="data">
        <thead><tr><th>Fuel</th><th class="t-right">${years[0]}</th><th class="t-right">${years[years.length - 1]}</th><th class="t-right">Change</th></tr></thead>
        <tbody>${(['bev', 'hybrid', 'diesel', 'petrol', 'other'] as Fuel[]).map(rowFor).join('')}</tbody>
      </table>
    </div>
  `;
  root.appendChild(changePanel);
}
